
import React from 'react';
import { Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import PlanFeatures from './PlanFeatures';
import PlanFeatureIcon from './PlanFeatureIcon';

interface PlanCardProps {
  plan: SubscriptionPlan;
  selectedPlanId: string;
  openCollapsibles: Record<string, boolean>;
  toggleCollapsible: (planId: string) => void;
  price: number;
  billingLabel: string;
  keyFeatures: Array<{ name: string, included: boolean }>;
  otherFeatures: Array<{ name: string, included: boolean }>;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  selectedPlanId,
  openCollapsibles,
  toggleCollapsible,
  price,
  billingLabel,
  keyFeatures,
  otherFeatures
}) => {
  // Format price to remove decimals
  const formatPrice = (price: number) => {
    return Math.round(price);
  };

  return (
    <div className="relative">
      <RadioGroupItem value={plan.id} id={plan.id} className="sr-only" />
      <Label 
        htmlFor={plan.id} 
        className={cn(
          "flex flex-col h-full cursor-pointer rounded-lg border-2 bg-white p-4 hover:bg-gray-50 transition-colors", 
          selectedPlanId === plan.id ? "border-primary shadow-sm" : "border-gray-200"
        )}
      >
        {plan.recommended && (
          <div className="absolute -top-3 -right-3 bg-primary text-white text-xs px-2 py-1 rounded-full font-medium">
            Recommended
          </div>
        )}
        
        <CardHeader className="pb-2 pt-0 px-0">
          <CardTitle className="text-lg">{plan.name}</CardTitle>
          <CardDescription className="line-clamp-2">{plan.description}</CardDescription>
        </CardHeader>
        
        <CardContent className="px-0 pb-0 flex-grow">
          <div className="mb-4">
            <span className="text-3xl font-bold">${formatPrice(price)}</span>
            <span className="text-gray-500 ml-1">{billingLabel}</span>
          </div>
          
          <div className="space-y-2">
            {/* Key plan limits always visible */}
            <div className="flex items-center text-sm">
              <PlanFeatureIcon featureName="referrals" />
              <span>Up to <strong>{plan.maxReferrals}</strong> referrals</span>
            </div>
            <div className="flex items-center text-sm">
              <PlanFeatureIcon featureName="managers" />
              <span>Up to <strong>{plan.maxManagers}</strong> managers</span>
            </div>
            <div className="flex items-center text-sm">
              <PlanFeatureIcon featureName="links" />
              <span>Up to <strong>{plan.maxReferralLinks}</strong> referral links</span>
            </div>
          </div>
          
          <PlanFeatures
            planId={plan.id}
            keyFeatures={keyFeatures}
            otherFeatures={otherFeatures}
            openCollapsibles={openCollapsibles}
            toggleCollapsible={toggleCollapsible}
          />
        </CardContent>
        
        {selectedPlanId === plan.id && (
          <div className="absolute top-2 right-2">
            <Check className="h-5 w-5 text-primary" />
          </div>
        )}
      </Label>
    </div>
  );
};

export default PlanCard;
